import { useState, useEffect } from 'react';
// hooks
import useResponsive from '../../hooks/useResponsive';
// components
import Breadcrumbs from '../../components/breadcrumbs';
import TableInfo from '../../components/table-info';
import Pagination from '../../components/pagination';
import Dialog from '../../components/dialog';
// @devextreme
import DateBox from 'devextreme-react/date-box';
import DateRangeBox from 'devextreme-react/date-range-box';
import { Template } from 'devextreme-react/core/template';
import DataGrid, { Column } from 'devextreme-react/data-grid';
// @moment
import moment from 'moment-timezone';
// utils
import { timezones } from '../../utils/format';
// assets
import Calendar from '../../assets/button/Calendar';
// styles
import { Container, SearchWrapper, Tabs, Box, PrimaryButton } from '../../styles';
// apis
import { getLoginLog } from '../../apis';

// ----------------------------------------------------------------------

const intervals = [{ value: 'weekly', name: '최근 1주일' }, { value: 'monthly', name: '최근 1개월' }, { value: 'setting', name: '직접 설정' }];

const sizes = [10, 20, 30];
const utc = new Date(timezones(moment().utc().format(), 'YYYY-MM-DDTHH:mm:ss'));
const weeklyDate = timezones(moment().utc().subtract(7, 'days').format(), 'YYYY-MM-DDTHH:mm:ss');

// ----------------------------------------------------------------------

export default function LoginLogPage() {
  const mobile = useResponsive('down', 'md');

  const [ interval, setInterval ] = useState('weekly');

  const [ startDate, setStartDate ] = useState(new Date(weeklyDate));
  const [ endDate, setEndDate ] = useState(new Date(utc));
  const [ searched, setSearched ] = useState({ start: null, end: null });
  
  const [ data, setData ] = useState({ content: [], totalElements: 0, totalPages: 1 });
  const { content: rows, totalElements: total, totalPages: pages } = data;

  const [ page, setPage ] = useState(0);
  const [ size, setSize ] = useState(20);

  const [ message, setMessage ] = useState(null);
  const handleClose = () => setMessage(null);

  useEffect(() => {
    const now = timezones(moment().utc().format(), 'YYYY-MM-DDTHH:mm:ss');
    const monthly = timezones(moment().utc().subtract(1, 'months').format(), 'YYYY-MM-DDTHH:mm:ss');
    const weekly = timezones(moment().utc().subtract(7, 'days').format(), 'YYYY-MM-DDTHH:mm:ss');
    if ( interval === 'weekly' ) {
      setStartDate(new Date(weekly));
      setEndDate(new Date(now));
      handleSearch(new Date(weekly), new Date(now));
    } else if ( interval === 'monthly' ) {
      setStartDate(new Date(monthly));
      setEndDate(new Date(now));
      handleSearch(new Date(monthly), new Date(now));
    }
  }, [interval]);

  const handlePage = (value: number) => {
    setPage(value-1);
    handleSearch(searched.start, searched.end, { page: value-1, size });
  };

  const handleSize = (value: number) => {
    setPage(0);
    setSize(value);
    handleSearch(searched.start, searched.end, { page: 0, size: value });
  };

  const handleSearch = (start?: Date, end?: Date, value?: any) => {

    if ( interval === 'setting' ) {
      const difference = moment(endDate).diff(moment(startDate), 'months');
      if ( difference > 2 ) return setMessage('3개월 이내로 검색해 주세요.');
    }

    if ( !value ) setPage(0);

    getLoginLog(setData, start || startDate, end || endDate, value?.page || 0, value?.size || size)
      .then(() => setSearched({start, end}))
      .catch(() => setData({ content: [], totalElements: 0, totalPages: 1 }));
  };

  const cellRender = ({data}: any) => {
    return timezones(data.accessAt, 'YYYY-MM-DD HH:mm:ss');
  };

  return (
    <Container>
      <Breadcrumbs heading='로그인 조회' />

      <SearchWrapper>
        <div>
          <Tabs>
            { intervals.map((item) => (
              <button key={item.value} onClick={() => setInterval(item.value)} className={interval === item.value ? 'active' : ''} style={{'width': mobile ? '33.333%': ''}}>{item.name}</button>
            ))}
          </Tabs>

          { !mobile ? (
            <DateRangeBox
              value={[startDate, endDate]}
              onValueChanged={(event: any) => {setStartDate(event.value[0]); setEndDate(event.value[1]);}}
              displayFormat='yyyy-MM-dd'
              openOnFieldClick={true}
              useMaskBehavior={true}
              startDateLabel=''
              endDateLabel=''
              disabled={interval === 'setting' ? false : true}
              max={utc}
              dropDownButtonTemplate='calendar'
            >
              <Template name='calendar' render={Calendar} />
            </DateRangeBox>
          ) : (
            <div className={`rangeBox${ interval === 'setting' ? '' : ' disabled'}`} style={{'width': '100%'}}>
              <DateBox
                type='date'
                value={startDate}
                onValueChanged={(event) => setStartDate(event.value)}
                displayFormat='yyyy-MM-dd'
                elementAttr={{class: 'calendarStart'}}
                openOnFieldClick={true}
                useMaskBehavior={true}
                pickerType='rollers'
                max={utc}
                disabled={interval === 'setting' ? false : true}
              />
              <p className='range'>~</p>
              <DateBox
                type='date'
                value={endDate}
                onValueChanged={(event) => setEndDate(event.value)}
                displayFormat='yyyy-MM-dd'
                elementAttr={{class: 'calendarEnd'}}
                openOnFieldClick={true}
                useMaskBehavior={true}
                pickerType='rollers'
                max={utc}
                dropDownButtonTemplate='calendar'
              disabled={interval === 'setting' ? false : true}
              >
                <Template name='calendar' render={Calendar} />
              </DateBox>
            </div>
          )}

          <PrimaryButton type='button' onClick={() => handleSearch()} disabled={interval === 'setting' ? false : true}>검색</PrimaryButton>
        </div>
      </SearchWrapper>
      
      <Box>
        <TableInfo total={total} pages={pages} page={page} sizes={sizes} size={size} handleSize={handleSize} />

        <DataGrid
          dataSource={rows}
          noDataText=''
          columnAutoWidth={true}
          paging={{enabled: false}}
        >
          <Column caption='일시' dataField='accessAt' alignment='center' cellRender={cellRender} />
          <Column caption='접속 유형' dataField='device' alignment='center' />
          <Column caption='운영 체제' dataField='os' alignment='center' />
          <Column caption='브라우저 / 앱' dataField='browser' alignment='center' />
          <Column caption='로그인 IP' dataField='ip' alignment='center' />
          <Column caption='상태' dataField='status' alignment='center' />
        </DataGrid>

        <Pagination page={page} pages={pages} size={size} handlePage={handlePage} />
      </Box>

      { message &&
        <Dialog
          handleClose={handleClose}
        >
          <p>{ message }</p>
        </Dialog>
      }
    </Container>
  );
}